import { useState, useEffect, useRef } from 'react';
import useAPI from './useApi';
import moment from 'moment-timezone';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Hook to fetch trades for a specific date
 * 
 * @param {Object} account - Selected account object
 * @param {string} tradeDate - Date in YYYY-MM-DD format (defaults to today)
 * @param {number} limit - Maximum number of trades to fetch (default: 100)
 * @returns {Object} { trades, total, loading, error }
 */
const useTradesForDate = (account, tradeDate = null, limit = 100) => {
  const [trades, setTrades] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { request } = useAPI();
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;

    const fetchTrades = async () => {
      if (!isMountedRef.current) return;

      // Don't fetch if no account (also used to skip while modal closed)
      if (!account?.AccountID) {
        setTrades([]);
        setTotal(0);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        // Default to today if no date provided
        const dateToFetch = tradeDate || moment().tz('America/New_York').format('YYYY-MM-DD');
        const accountId = account.AccountID;

        const response = await request(
          `${BACKEND_URL}/trading_rules_trades?account_id=${accountId}&trade_date=${dateToFetch}&limit=${limit}`,
          {},
          'GET'
        );

        if (!isMountedRef.current) return;

        setTrades(response?.trades || []);
        setTotal(response?.total || 0);
      } catch (err) {
        console.error('[useTradesForDate] Error fetching trades:', err);
        if (isMountedRef.current) {
          setError(err.message || 'Failed to load trades');
        }
      } finally {
        if (isMountedRef.current) {
          setLoading(false);
        }
      }
    };

    fetchTrades();

    return () => {
      isMountedRef.current = false;
    };
  }, [account?.AccountID, tradeDate, limit, request]);

  return {
    trades,
    total,
    loading,
    error
  };
};

export default useTradesForDate;
